import React, { useState } from 'react';
import {
  Upload,
  Download,
  FileText,
  CheckCircle,
  AlertTriangle,
  X,
  Loader,
  Info
} from 'lucide-react';

const BulkMonitorUpload = ({ user, onSuccess, onClose }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [template, setTemplate] = useState(null);

  const fetchTemplate = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/monitor-template`);

      console.log('Template response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Template data received:', data);
        setTemplate(data);
      } else {
        const errorData = await response.json();
        console.error('Template fetch error:', errorData);
        setError(`Failed to fetch template: ${errorData.detail}`);
      }
    } catch (err) {
      console.error('Failed to fetch template:', err);
      setError('Failed to fetch template');
    }
  };

  React.useEffect(() => {
    fetchTemplate();
  }, []);

  const downloadTemplate = () => {
    if (!template) return;

    const blob = new Blob([template.content], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = template.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setError('');
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a CSV file');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/monitors/bulk-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data);
        if (data.successful > 0 && onSuccess) {
          onSuccess();
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Upload failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const resetUpload = () => {
    setFile(null);
    setResult(null);
    setError('');
    // Reset file input
    const fileInput = document.getElementById('csv-upload');
    if (fileInput) fileInput.value = '';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <Upload className="w-6 h-6 text-blue-600 mr-2" />
              <h2 className="text-xl font-bold text-gray-900">Bulk Monitor Upload</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Template Download */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 mb-6">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                <Info className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 mb-2">📋 Step 1: Download Template</h3>
                <p className="text-sm text-blue-800 mb-4">
                  Start by downloading our CSV template to see the required format for bulk uploads.
                </p>
                <button
                  onClick={downloadTemplate}
                  disabled={!template}
                  className="inline-flex items-center bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 text-sm font-medium shadow-md"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download CSV Template
                </button>
              </div>
            </div>
          </div>

          {/* Template Instructions */}
          {template && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-6">
              <h3 className="flex items-center font-semibold text-gray-900 mb-4">
                <FileText className="w-5 h-5 mr-2 text-gray-600" />
                📝 CSV Format Requirements
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {Object.entries(template.instructions || {}).map(([field, description]) => (
                  <div key={field} className="bg-white p-3 rounded-lg border border-gray-100">
                    <span className="font-semibold text-gray-900 text-xs uppercase tracking-wide">{field}</span>
                    <p className="text-gray-700 mt-1">{description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* File Upload */}
          {!result && (
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-dashed border-gray-300 rounded-xl p-8 text-center mb-6 hover:border-blue-400 transition-colors">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full flex items-center justify-center">
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">📂 Step 2: Upload Your CSV File</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Select your completed CSV file with monitor data
                  </p>
                </div>

                <div>
                  <input
                    id="csv-upload"
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <label
                    htmlFor="csv-upload"
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 cursor-pointer transition-all transform hover:scale-105 font-medium shadow-lg"
                  >
                    <Upload className="w-5 h-5 mr-2" />
                    Choose CSV File
                  </label>
                </div>

                {file && (
                  <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl">
                    <div className="flex items-center justify-center">
                      <div className="flex items-center bg-white px-4 py-2 rounded-lg shadow-sm">
                        <FileText className="w-5 h-5 text-green-600 mr-3" />
                        <div className="text-left">
                          <div className="font-medium text-green-800">{file.name}</div>
                          <div className="text-xs text-green-600">Ready for upload</div>
                        </div>
                        <button
                          onClick={resetUpload}
                          className="ml-4 text-green-600 hover:text-green-800 p-1 rounded-full hover:bg-green-100 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Upload Results */}
          {result && (
            <div className="space-y-4 mb-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  <h3 className="font-medium text-green-800">Upload Complete!</h3>
                </div>
                
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">{result.total_processed}</div>
                    <div className="text-gray-600">Total Processed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{result.successful}</div>
                    <div className="text-gray-600">Successful</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">{result.failed}</div>
                    <div className="text-gray-600">Failed</div>
                  </div>
                </div>
              </div>

              {/* Created Monitors */}
              {result.created_monitors && result.created_monitors.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Created Monitors ({result.created_monitors.length})</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {result.created_monitors.map((monitor, index) => (
                      <div key={index} className="flex items-center justify-between bg-white p-2 rounded border">
                        <div>
                          <div className="font-medium text-sm">{monitor.name}</div>
                          <div className="text-xs text-gray-500">{monitor.url}</div>
                        </div>
                        <div className="text-xs text-gray-500">
                          {monitor.check_interval}s interval
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Errors */}
              {result.errors && result.errors.length > 0 && (
                <div className="bg-red-50 rounded-lg p-4">
                  <h4 className="font-medium text-red-800 mb-3">Errors ({result.errors.length})</h4>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {result.errors.map((error, index) => (
                      <div key={index} className="text-sm text-red-700 bg-white p-2 rounded border">
                        {error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Subscription Limits Info */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6 mb-6">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mr-4">
                <Info className="w-5 h-5 text-amber-600" />
              </div>
              <div className="text-sm text-amber-800">
                <h4 className="font-semibold mb-2">⚡ Your Subscription Limits</h4>
                <div className="bg-white bg-opacity-50 rounded-lg p-3 mb-3">
                  <p className="font-medium">
                    {user?.subscription_tier === 'free' && '🆓 Free Plan: 1 monitor total'}
                    {user?.subscription_tier === 'pro' && '⭐ Pro Plan: 10 monitors total'}  
                    {user?.subscription_tier === 'business' && '🚀 Business Plan: 50 monitors total'}
                    {user?.subscription_tier === 'enterprise' && '🏢 Enterprise Plan: 500 monitors total'}
                  </p>
                </div>
                <p>📊 Upload will be limited to available slots in your current plan.</p>
                {user?.subscription_tier === 'free' && (
                  <p className="mt-2 text-amber-700">
                    💡 <strong>Need more monitors?</strong> Consider upgrading to Pro or Business plan for more capacity!
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            {result ? (
              <>
                <button
                  onClick={resetUpload}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Upload Another File
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Done
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={!file || uploading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {uploading ? (
                    <div className="flex items-center">
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Monitors
                    </div>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkMonitorUpload;